import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Aviation, Driver, Order, User, File } from "../services";
import { groupChatMessages } from "../utils/chatGrouper";
import { useSelector } from "react-redux";
import { useChat } from "../context/chat.context";
import chatHub from "../services/ChatHub";
import { useLanguage } from "../context/LanguageContext";
import { getChatFileDownloadName } from "../utils/chatFileUtils";

export const useOrderChats = ({ isOpen, orderId, orderData: initialOrderData, t, serviceType = "order" }) => {
  const { currentLanguage } = useLanguage();
  const { registerChat, unregisterChat } = useChat();
  const { user } = useSelector((state) => state);
  const company = useSelector((state) => state.user.company);
  const chatService = serviceType === "aviation" ? Aviation : Order;

  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(initialOrderData || null);
  const [orderNumber, setOrderNumber] = useState("");
  const [chatFiles, setChatFiles] = useState({});
  const [chatFilesLoading, setChatFilesLoading] = useState(true);
  const [chatFilesLoadedOrderId, setChatFilesLoadedOrderId] = useState(null);
  const chatFilesRequestCountRef = useRef(0);

  const createEmptyChat = useCallback(
    ({ id, name, role, type, companyId, pinned = false }) => ({
      id,
      name,
      role,
      type,
      companyId,
      messages: [],
      lastMessage: "",
      time: "",
      unreadCount: 0,
      pinned,
    }),
    [],
  );

  // Создание пустого чата для Expeditor -> Client
  const createExpeditorToClientChat = useCallback(
    async (order, groupedChats) => {
      if (
        user?.type !== "Expeditor" ||
        groupedChats.length !== 0 ||
        !(
          (order.status === "assigned" ||
            order.status === "awaitingoffer" ||
            order.expeditorId === company?.id) &&
          order.companyId !== company?.id
        )
      ) {
        return null;
      }

      try {
        const companyResponse = await User.getUsersByCompany(order.companyId);
        const companyData = companyResponse.data;

        return createEmptyChat({
          id: order.companyId,
          name: companyData.name || t("registrationSurveyUserSecond.orgType.shipper"),
          role: t("registrationSurveyUserSecond.orgType.shipper"),
          type: "Client",
          companyId: order.companyId,
        });
      } catch (err) {
        console.error("Error fetching client company info:", err);
        return null;
      }
    },
    [user?.type, company?.id, createEmptyChat, t],
  );

  // Создание пустого чата для Client -> Expeditor
  const createClientToExpeditorChat = useCallback(
    async (order, groupedChats) => {
      if (user?.type !== "Client" || !order.expeditorId) {
        return null;
      }

      const expeditorChatExists = groupedChats.some((chat) => chat.id === order.expeditorId);
      if (expeditorChatExists) {
        return null;
      }

      try {
        const companyResponse = await User.getUsersByCompany(order.expeditorId);
        const companyData = companyResponse.data;

        return createEmptyChat({
          id: order.expeditorId,
          name: companyData.name || t("registrationSurveyUserSecond.orgType.transporter"),
          role: t("registrationSurveyUserSecond.orgType.transporter"),
          type: "Expeditor",
          companyId: order.expeditorId,
          pinned: true,
        });
      } catch (err) {
        console.error("Error fetching expeditor company info:", err);
        return null;
      }
    },
    [user?.type, createEmptyChat, t],
  );

  // Создание пустого чата для Expeditor -> Driver
  const createExpeditorToDriverChat = useCallback(
    async (order, groupedChats) => {
      if (user?.type !== "Expeditor" || !order.driverId) {
        return null;
      }

      const driverChatExists = groupedChats.some((chat) => chat.id === order.driverId);
      if (driverChatExists) {
        return null;
      }

      try {
        const driverResponse = await Driver.getDriverInfo(order.driverId);
        const driverData = driverResponse.data;

        const driverFullName = [driverData.surname, driverData.name, driverData.patronymic]
          .filter(Boolean)
          .join(" ")
          .trim();

        return createEmptyChat({
          id: order.driverId,
          name: driverFullName || t("orderInfo.ordersDriverInfo.driverLabel"),
          role: t("orderInfo.ordersDriverInfo.driverLabel"),
          type: "driver",
          companyId: order.driverId,
        });
      } catch (err) {
        console.error("Error fetching driver info:", err);
        return null;
      }
    },
    [user?.type, createEmptyChat, t],
  );

  const fetchChatFiles = useCallback(async () => {
    if (!orderId) return;

    chatFilesRequestCountRef.current += 1;
    setChatFilesLoading(true);

    try {
      const response = await File.getChatFilesByOrder(orderId);
      const responseFiles = response.data || [];
      const files = Array.isArray(responseFiles) ? responseFiles : [responseFiles];

      const groupedFiles = {};
      files.forEach((file) => {
        if (file.chatId) {
          if (!groupedFiles[file.chatId]) {
            groupedFiles[file.chatId] = [];
          }
          groupedFiles[file.chatId].push(file);
        }
      });

      setChatFiles(groupedFiles);
      setChatFilesLoadedOrderId(orderId);
    } catch (err) {
      console.error("Error fetching chat files:", err);
    } finally {
      chatFilesRequestCountRef.current -= 1;
      if (chatFilesRequestCountRef.current === 0) {
        setChatFilesLoading(false);
      }
    }
  }, [orderId]);

  const downloadChatFile = useCallback(async (chatId, contentType, fileName, returnBlob = false) => {
    try {
      const response = await File.downloadChatFile(chatId);
      const blob = new Blob([response.data], { type: contentType });
      
      if (returnBlob) {
        return blob;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getChatFileDownloadName(chatId, fileName, contentType);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (err) {
      console.error("Error downloading file:", err);
      throw err;
    }
  }, []);

  const uploadChatFile = useCallback(
    async (file, messageId) => {
      const formData = new FormData();
      const contentType = file.type || "application/octet-stream";

      formData.append("File", file, file.name);
      formData.append("orderId", orderId);
      formData.append("chatId", messageId);
      formData.append("ContentType", contentType);
      formData.append("FileName", file.name);
      formData.append("Name", file.name);

      try {
        const response = await File.uploadChatFile(formData);
        return response.data;
      } catch (err) {
        console.error("Error uploading file:", err);
        throw err;
      }
    },
    [orderId],
  );

  const fetchChats = useCallback(
    async (silent = false) => {
      if (!orderId) return;

      if (!silent) {
        setLoading(true);
      }

      try {
        if (!silent) {
          setSelectedChat(null);
        }
        let currentOrderData = orderData;
        if (!currentOrderData || currentOrderData.id !== orderId) {
          try {
            const orderResponse =
              serviceType === "aviation"
                ? await Aviation.getAirOrdersById(orderId, currentLanguage)
                : await Order.getOrderById(orderId, currentLanguage);
            currentOrderData = orderResponse.data;
            setOrderData(currentOrderData);
            setOrderNumber(currentOrderData.number);
          } catch (err) {
            setOrderData(initialOrderData || null);
            setOrderNumber("");
            console.error("Error fetching order data:", err);
          }
        }

        let groupedChats = [];
        try {
          const response = await chatService.getOrderChat(orderId);
          groupedChats = groupChatMessages(response.data, user?.id, company?.id, t);
        } catch (err) {
          console.error("Error fetching order chats:", err);
          groupedChats = [];
        }

        if (currentOrderData) {
          // Создание пустых чатов
          const expeditorToClientChat = await createExpeditorToClientChat(currentOrderData, groupedChats);
          if (expeditorToClientChat) {
            groupedChats.push(expeditorToClientChat);
          }

          const clientToExpeditorChat = await createClientToExpeditorChat(currentOrderData, groupedChats);
          if (clientToExpeditorChat) {
            groupedChats.unshift(clientToExpeditorChat);
          }

          const expeditorToDriverChat = await createExpeditorToDriverChat(currentOrderData, groupedChats);
          if (expeditorToDriverChat) {
            groupedChats.push(expeditorToDriverChat);
          }

          // Пометка и сортировка закреплённых чатов
          if (currentOrderData.expeditorId) {
            groupedChats.forEach((chat) => {
              if (chat.companyId === currentOrderData.expeditorId) {
                chat.pinned = true;
              }
            });

            groupedChats.sort((a, b) => {
              if (a.pinned && !b.pinned) return -1;
              if (!a.pinned && b.pinned) return 1;
              return new Date(b.lastMessageDate) - new Date(a.lastMessageDate);
            });
          }
        }

        setChats(groupedChats);

        if (selectedChat) {
          const updatedSelectedChat = groupedChats.find((chat) => chat.id === selectedChat.id);
          if (updatedSelectedChat) {
            setSelectedChat(updatedSelectedChat);
          }
        } else if (groupedChats.length > 0) {
          setSelectedChat(groupedChats[0]);
        }
      } catch (error) {
        console.error("Error loading chats:", error);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [
      orderId,
      user?.id,
      company?.id,
      selectedChat?.id,
      orderData,
      t,
      createExpeditorToClientChat,
      createClientToExpeditorChat,
      createExpeditorToDriverChat,
      chatService,
    ],
  );

  const markMessagesAsRead = useCallback(async () => {
    if (!selectedChat || !selectedChat.messages) return;

    const unreadMessages = selectedChat.messages.filter((message) => !message.sent && !message.isRead);

    if (unreadMessages.length === 0) return;

    try {
      await Promise.all(unreadMessages.map((message) => chatService.readOrderChat(message.id)));

      setSelectedChat((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) => (!msg.sent && !msg.isRead ? { ...msg, isRead: true } : msg)),
        unreadCount: 0,
      }));

      setChats((prev) =>
        prev.map((chat) => (chat.id === selectedChat.id ? { ...chat, unreadCount: 0 } : chat)),
      );
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  }, [selectedChat, chatService]);

  const sendMessage = useCallback(
    async (messageText, files = []) => {
      if ((!messageText?.trim() || !selectedChat) && files.length === 0) return;

      const text = messageText?.trim() || "";

      if (files.length > 0) {
        const tempMessageId = `temp_${Date.now()}`;
        
        const localFiles = files.map((file, index) => ({
          id: `temp_file_${tempMessageId}_${index}`,
          chatId: tempMessageId,
          contentType: file.type || "application/octet-stream",
          fileName: file.name,
          isLocal: true,
        }));

        const newMessage = {
          id: tempMessageId,
          text: "",
          messageType: "file",
          time: new Date().toLocaleTimeString().slice(0, 5),
          sent: true,
          status: "sending",
          createdAt: new Date().toISOString(),
          hasFiles: true,
          files: localFiles,
        };

        setSelectedChat((prev) => ({
          ...prev,
          messages: [...prev.messages, newMessage],
          lastMessage: t("orderChat.file"),
        }));

        setChats((prev) =>
          prev.map((chat) =>
            chat.id === selectedChat.id
              ? { ...chat, lastMessage: t("orderChat.file"), messages: [...chat.messages, newMessage] }
              : chat,
          ),
        );

        const payload = {
          orderId,
          receiverId: selectedChat.id,
          text: "",
          receiver: selectedChat.type === "driver" ? "driver" : "",
          messageType: "file",
        };

        try {
          const res = await chatService.sendMessage(payload);
          const messageId = res.data?.id || tempMessageId;

          for (const file of files) {
            await uploadChatFile(file, messageId);
          }

          const updatedMessage = {
            ...newMessage,
            id: messageId,
            status: "sent",
            files: localFiles.map((f, index) => ({
              ...f,
              id: `file_${messageId}_${index}`,
              chatId: messageId,
              isLocal: false,
            })),
          };

          setChatFiles((prev) => ({
            ...prev,
            [messageId]: localFiles.map((f, index) => ({
              ...f,
              id: `file_${messageId}_${index}`,
              chatId: messageId,
              isLocal: false,
            })),
          }));

          setSelectedChat((prev) => ({
            ...prev,
            messages: prev.messages.map((msg) => 
              msg.id === tempMessageId ? updatedMessage : msg
            ),
          }));

          setChats((prev) =>
            prev.map((chat) =>
              chat.id === selectedChat.id
                ? {
                    ...chat,
                    lastMessage: t("orderChat.file"),
                    messages: chat.messages.map((msg) =>
                      msg.id === tempMessageId ? updatedMessage : msg
                    ),
                  }
                : chat,
            ),
          );

          return true;
        } catch (error) {
          console.error("Error sending file message:", error);
          setSelectedChat((prev) => ({
            ...prev,
            messages: prev.messages.filter((msg) => msg.id !== tempMessageId),
          }));
          return false;
        }
      }

      const payload = {
        orderId,
        receiverId: selectedChat.id,
        text,
        receiver: selectedChat.type === "driver" ? "driver" : "",
        messageType: "text",
      };

      try {
        const res = await chatService.sendMessage(payload);

        const newMessage = {
          id: res.data?.id || Date.now(),
          text,
          messageType: "text",
          time: new Date().toLocaleTimeString().slice(0, 5),
          sent: true,
          status: "sent",
          createdAt: new Date().toISOString(),
        };

        setSelectedChat((prev) => ({
          ...prev,
          messages: [...prev.messages, newMessage],
          lastMessage: text,
        }));

        setChats((prev) =>
          prev.map((chat) =>
            chat.id === selectedChat.id
              ? { ...chat, lastMessage: text, messages: [...chat.messages, newMessage] }
              : chat,
          ),
        );

        return true;
      } catch (error) {
        console.error("Error sending message:", error);
        return false;
      }
    },
    [orderId, selectedChat, chatService, uploadChatFile, t],
  );

  useEffect(() => {
    if (isOpen && orderId) {
      fetchChats();
    }
  }, [isOpen, orderId]);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchChatFiles();
    }
  }, [isOpen, orderId, fetchChatFiles]);

  // Регистрация чата для WebSocket
  useEffect(() => {
    if (isOpen && orderId) {
      const silentFetchChats = () => fetchChats(true);
      registerChat(orderId, silentFetchChats, true);

      return () => {
        unregisterChat(orderId);
      };
    }
  }, [isOpen, orderId, fetchChats, registerChat, unregisterChat]);

  useEffect(() => {
    markMessagesAsRead();
  }, [selectedChat?.id, markMessagesAsRead]);

  useEffect(() => {
    if (!isOpen || !orderId) return;

    const handleOrderMessage = async (data) => {
      const isReceiver = data.receiverId === user?.id || data.receiverCompanyId === company?.id;

      if (data.orderId === orderId && isReceiver) {
        await fetchChats(true);
        await fetchChatFiles();
        markMessagesAsRead();
        setTimeout(async () => {
          await fetchChatFiles();
        }, 2000);
      }
    };

    const unsubscribe = chatHub.subscribe("order-message-chat", handleOrderMessage);

    return () => {
      unsubscribe();
    };
  }, [isOpen, orderId, fetchChats, fetchChatFiles, markMessagesAsRead, user?.id, company?.id]);

  const canSendMessage = useMemo(() => {
    if (!selectedChat || !orderData) return false;

    const isOrderCreator = company?.id === orderData.companyId;

    if (isOrderCreator && orderData.expeditorId) {
      return selectedChat.companyId === orderData.expeditorId;
    }

    return true;
  }, [selectedChat, orderData, company?.id]);

  return {
    chats,
    selectedChat,
    setSelectedChat,
    loading,
    orderNumber,
    sendMessage,
    markMessagesAsRead,
    fetchChats,
    fetchChatFiles,
    downloadChatFile,
    chatFiles,
    isChatFilesLoading: chatFilesLoading || chatFilesLoadedOrderId !== orderId,
    canSendMessage,
  };
};

export default useOrderChats;
